import { useMutation, useQuery } from "@tanstack/react-query";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import type { Schemas } from "../workspace/api.generated";
import * as ipAllowlistApi from "./api";

export type IpAllowlistMutation =
  | { readonly kind: "create"; readonly body: Schemas["CreateAdminIpAllowlistRequest"] }
  | { readonly kind: "deactivate"; readonly id: number };

export const ipAllowlistKeys = {
  all: (epoch: number) => ["admin", epoch, "ip-allowlist"] as const,
  list: (epoch: number) => [...ipAllowlistKeys.all(epoch), "list"] as const,
};

export function useIpAllowlist() {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: ipAllowlistKeys.list(session.sessionEpoch),
    queryFn: ({ signal }) => ipAllowlistApi.getIpAllowlist(apiClient, signal),
    staleTime: 0,
  });
}

export function useIpAllowlistMutation() {
  const { apiClient, queryClient, sessionLifecycle } = useAdminApplication();
  const session = useAdminSession();
  return useMutation({
    mutationFn: async (input: IpAllowlistMutation) => {
      const request = sessionLifecycle.createRequestController();
      try {
        if (input.kind === "create") return await ipAllowlistApi.createIpAllowlistEntry(apiClient, input.body, request.controller.signal);
        return await ipAllowlistApi.deactivateIpAllowlistEntry(apiClient, input.id, request.controller.signal);
      } finally {
        request.release();
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ipAllowlistKeys.list(session.sessionEpoch) });
    },
  });
}
